// ============================================
// Memory Edit - Event Layer (ES6)
// 사건 레이어 UI
// ============================================

import { escapeHtml } from '../../utils.js';
import { renderEmptyState, renderLayerHeader, LAYER_CONFIG } from '../accordion.js';
import { getDisplayedCount } from '../state.js';

// ============================================
// 중요도 설정
// ============================================

export const IMPORTANCE_CONFIG = {
  high: { label: '높음', color: '#EF4444' },
  medium: { label: '보통', color: '#F59E0B' },
  low: { label: '낮음', color: '#6B7280' }
};

// ============================================
// Event 레이어 렌더링 (페이지네이션 적용)
// ============================================

export function renderEventLayer(data) {
  if (!data || data.length === 0) {
    return renderEmptyState('event');
  }

  // API에서 이미 최신순 (turn desc)으로 정렬됨
  const sortedData = data;
  const displayCount = getDisplayedCount('event');
  const displayedItems = sortedData.slice(0, displayCount);
  const hasMore = sortedData.length > displayCount;
  const remainingCount = sortedData.length - displayCount;

  return `
    ${renderLayerHeader('event')}
    <div class="memory-event-list">
      ${displayedItems.map(event => renderEventItem(event)).join('')}
    </div>
    ${hasMore ? `
      <button class="memory-load-more-btn" data-action="loadMore" data-layer="event">
        더보기 (${remainingCount}개 더)
      </button>
    ` : ''}
  `;
}

// ============================================
// Event 아이템 렌더링
// ============================================

function renderEventItem(event) {
  const importance = IMPORTANCE_CONFIG[event.importance] || IMPORTANCE_CONFIG.medium;
  const importanceKey = event.importance || 'medium';

  return `
    <div class="memory-layer-item event-item" data-id="${event.id}" data-layer="event" data-importance="${importanceKey}">
      <div class="event-header">
        <span class="event-importance" style="background: ${importance.color}20; color: ${importance.color}">
          ${importance.label}
        </span>
        <div class="event-main">
          <span class="event-title">${escapeHtml(event.title || '이벤트')}</span>
          <span class="event-time">${escapeHtml(event.worldTime || '')}</span>
        </div>
        <div class="memory-item-actions compact">
          <button class="memory-icon-btn" data-action="editMemory" data-layer="event" data-id="${event.id}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          <button class="memory-icon-btn danger" data-action="deleteMemory" data-layer="event" data-id="${event.id}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
      </div>

      <div class="event-desc">${escapeHtml(event.description || '')}</div>

      ${event.location ? `
        <div class="event-location">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          ${escapeHtml(event.location)}
        </div>
      ` : ''}

      ${event.keyQuotes && event.keyQuotes.length > 0 ? `
        <div class="event-quotes">
          ${event.keyQuotes.slice(0, 2).map(q => `
            <div class="event-quote">
              <span class="quote-speaker">${escapeHtml(q.speaker || '?')}</span>
              <span class="quote-text">"${escapeHtml(q.text)}"</span>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <div class="event-meta">
        <span class="event-turn">턴 ${event.turn || '?'}</span>
        ${event.participants && event.participants.length > 0 ? `
          <span class="event-participants">${event.participants.map(p => escapeHtml(p)).join(', ')}</span>
        ` : ''}
      </div>
    </div>
  `;
}

// ============================================
// Event 편집 폼 필드
// ============================================

export function getEventEditFields(data) {
  return [
    {
      name: 'title',
      label: '제목',
      type: 'text',
      value: data?.title || '',
      placeholder: '이벤트 제목',
      required: true
    },
    {
      name: 'description',
      label: '설명',
      type: 'textarea',
      value: data?.description || '',
      placeholder: '무슨 일이 있었는지 설명해주세요',
      required: true
    },
    {
      name: 'importance',
      label: '중요도',
      type: 'select',
      value: data?.importance || 'medium',
      options: Object.entries(IMPORTANCE_CONFIG).map(([value, config]) => ({
        value,
        label: config.label
      }))
    },
    {
      name: 'participants',
      label: '참가자 (쉼표로 구분)',
      type: 'text',
      value: (data?.participants || []).join(', '),
      placeholder: '예: 나, 수진'
    }
  ];
}

// ============================================
// Event 생성 폼 필드
// ============================================

export function getEventCreateFields() {
  return [
    {
      name: 'title',
      label: '제목',
      type: 'text',
      value: '',
      placeholder: '이벤트 제목',
      required: true
    },
    {
      name: 'description',
      label: '설명',
      type: 'textarea',
      value: '',
      placeholder: '무슨 일이 있었는지 설명해주세요',
      required: true
    },
    {
      name: 'importance',
      label: '중요도',
      type: 'select',
      value: 'medium',
      options: Object.entries(IMPORTANCE_CONFIG).map(([value, config]) => ({
        value,
        label: config.label
      }))
    },
    {
      name: 'participants',
      label: '참가자 (쉼표로 구분)',
      type: 'text',
      value: '',
      placeholder: '예: 나, 수진'
    }
  ];
}

// ============================================
// Event 데이터 검증
// ============================================

export function validateEventData(formData) {
  const errors = [];

  if (!formData.title?.trim()) {
    errors.push('제목을 입력해주세요');
  }
  if (!formData.description?.trim()) {
    errors.push('설명을 입력해주세요');
  }

  return errors;
}

// ============================================
// 폼 데이터 변환
// ============================================

export function transformEventFormData(formData) {
  return {
    title: formData.title?.trim(),
    description: formData.description?.trim(),
    importance: formData.importance || 'medium',
    participants: formData.participants ? formData.participants.split(',').map(p => p.trim()).filter(Boolean) : []
  };
}
